// src/generators/dzial3_funkcje/temat3_kwadratowa/podtemat3_iloczynowa_types/TransformFactoredToGeneralTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

/* =============================================================
   TASK: TransformFactoredToGeneralTask
   f(x)=a(x-x1)(x-x2)  →  f(x)=ax^2+bx+c
   Krok 2:
   - gdy Δ>0 (x±A)(x±B): FOIL (4 składniki) + redukcja
   - gdy Δ=0 (x-r)^2: wzór skróconego mnożenia
============================================================= */

/* ---------- helpers ---------- */
function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do { v = Math.floor(Math.random() * span) + min; } while (exclude.has(v));
  return v;
}
const par = (v) => (v < 0 ? `(${v})` : `${v}`);

function generalLatex(a, b, c) {
  const aStr = (a === 1 ? "" : (a === -1 ? "-" : String(a)));
  const bStr = b === 0 ? "" : (b > 0 ? ` + ${Math.abs(b) === 1 ? "" : Math.abs(b)}x` : ` - ${Math.abs(b) === 1 ? "" : Math.abs(b)}x`);
  const cStr = c === 0 ? "" : (c > 0 ? ` + ${Math.abs(c)}` : ` - ${Math.abs(c)}`);
  return `${aStr}x^2${bStr}${cStr}` || "0";
}
function factorLatex(a, x1, x2) {
  const aStr = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);
  const f = (r) => (r < 0 ? `\\big(x + ${Math.abs(r)}\\big)` : `\\big(x - ${r}\\big)`);
  if (x1 === x2) return `${aStr}${f(x1)}^{2}`;
  return `${aStr}${f(x1)}${f(x2)}`;
}

/* ---------- EquationInput helpers ---------- */
function getRaw(val) {
  if (typeof val === "string") return val;
  if (val && typeof val === "object") {
    for (const k of ["raw","value","text","input","expr","equation","latex"]) {
      const v = val[k];
      if (typeof v === "string" && v.trim() !== "") return v;
    }
    try { const s = String(val); if (s && s !== "[object Object]") return s; } catch {}
  }
  return "";
}
function toLatexFractionSafe(str) {
  if (!str) return "";
  let s = str.replace(/([+\-]?)\s*(\d+)\s+(\d+)\s*\/\s*(\d+)/g,
    (_, sign, whole, num, den) => `${sign}${whole}\\dfrac{${num}}{${den}}`
  );
  s = s.replace(/(?<!\\dfrac\{)(\d+)\/(\d+)/g, (_, num, den) => `\\dfrac{${num}}{${den}}`);
  return s;
}

/* ---------- numeric fallback ---------- */
function normalizeRHS(raw) {
  let s = getRaw(raw) || "";
  if (!s) return "";
  s = s.replace(/\\left\(/g,"(").replace(/\\right\)/g,")").replace(/\\cdot/g,"");
  s = s.replace(/\u2212/g,"-").replace(/²/g,"^2");
  s = s.replace(/^f\(x\)=/i,"").replace(/^y=/i,"");
  s = s.replace(/(\d),(\d)/g,"$1.$2");
  s = s.replace(/\s+/g,"");
  s = s.replace(/\(([-+]?(?:\d+\/\d+|\d*\.?\d+))\)/g,"$1");
  return s;
}
function explicitMultiply(expr) {
  let s = expr;
  s = s.replace(/(\d)(x)/g, "$1*$2");
  s = s.replace(/(\d)\(/g, "$1*(");
  s = s.replace(/\)(\d|x)/g, ")*$1");
  s = s.replace(/x\(/g, "x*(");
  return s;
}
function powToJS(e) { return e.replace(/\^/g, "**"); }
function safeEvalRHS(rhs, x) {
  let s = normalizeRHS(rhs);
  if (!s) return NaN;
  s = explicitMultiply(s);
  s = powToJS(s);
  const js = s.replace(/x/g, `(${x})`);
  try {
    // eslint-disable-next-line no-new-func
    const v = Function(`"use strict"; return (${js});`)();
    return Number.isFinite(v) ? v : NaN;
  } catch {
    return NaN;
  }
}
function numericallyEqGeneral(userRHS, a, b, c, tol = 1e-3) {
  const xs = [-3,-1,0,1,2,4,5];
  for (const x of xs) {
    const u = safeEvalRHS(userRHS, x);
    if (!Number.isFinite(u)) return false;
    const ref = a*x*x + b*x + c;
    if (Math.abs(u - ref) > tol) return false;
  }
  return true;
}

/* ---------- quick parse ax^2+bx+c ---------- */
const NUM = '(?:\\d+\\/\\d+|\\d*\\.\\d+|\\d+)';
function parseNumber(s) {
  if (s == null) return null;
  s = s.replace(/(\d),(\d)/g, "$1.$2").trim();
  if (!s) return null;
  if (/^[+\-]?\d*\.?\d+(?:e[+\-]?\d+)?$/i.test(s)) return parseFloat(s);
  const m = s.match(/^([+\-]?\d+)\s*\/\s*([+\-]?\d+)$/);
  if (m) {
    const p = parseInt(m[1],10), q = parseInt(m[2],10);
    if (!q) return null;
    return p/q;
  }
  return null;
}
function parseGeneralUserRaw(raw) {
  let s = normalizeRHS(raw);
  if (!s) return null;

  if (!/^[+\-]/.test(s)) s = `+${s}`;
  s = s.replace(/([+\-])x\^2/g, "$11x^2");
  s = s.replace(/([+\-])x(?!\^)/g, "$11x");

  let a = 0, b = 0, c = 0;
  const reX2 = new RegExp(`([+\\-])(${NUM})x\\^2`, "g");
  const reX  = new RegExp(`([+\\-])(${NUM})x(?!\\^)`, "g");
  const reC  = new RegExp(`([+\\-])(${NUM})(?![x/])`, "g");

  s.replace(reX2, (_, sign, num) => { const v = parseNumber(sign + num); if (v!=null) a += v; return _; });
  s.replace(reX,  (_, sign, num) => { const v = parseNumber(sign + num); if (v!=null) b += v; return _; });
  s.replace(reC,  (_, sign, num) => { const v = parseNumber(sign + num); if (v!=null) c += v; return _; });

  if (![a,b,c].every(Number.isFinite)) return null;
  return { a, b, c };
}

/* ---------- generator ---------- */
export function generateTask() {
  const a = randInt(-6, 6, new Set([0]));
  const deltaZero = Math.floor(Math.random() * 6) === 0;

  let x1 = randInt(-6, 6, new Set([0]));
  let x2 = deltaZero ? x1 : randInt(-6, 6, new Set([0, x1]));

  const b = -a * (x1 + x2);
  const c =  a * x1 * x2;

  return {
    type: "TransformFactoredToGeneralTask",
    data: { a, b, c, x1, x2, deltaZero,
      factoredLatex: factorLatex(a, x1, x2)
    }
  };
}

/* ---------- render ---------- */
export function renderTask({ data }) {
  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div>
        Dana jest funkcja w postaci iloczynowej:{" "}
        <InlineMath math={`f(x) = ${data.factoredLatex}`} />
      </div>
      <div>
        Zapisz ją w postaci ogólnej <InlineMath math={'f(x) = ax^2 + bx + c'} />.
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const raw = getRaw(userAnswer);
  const hasText = raw.trim().length > 0;

  return (
    <div style={{ display: "grid", gap: 8, marginTop: 6 }}>
      <div><InlineMath math={'f(x) ='} /></div>
      <EquationInput
        value={raw}
        onChange={(val) => onChange(val)}
        disabled={disabled}
        placeholder="np. 2x^2 - 3x + 5"
        showPreview={false}
      />
      {hasText && (
        <div style={{ opacity: 0.85 }}>
          <span style={{ marginRight: 6 }}>Podgląd:</span>
          <InlineMath math={`f(x) = ${toLatexFractionSafe(raw)}`} />
        </div>
      )}
    </div>
  );
}

/* ---------- check + answers ---------- */
export function checkAnswer(arg1, arg2) {
  const task = arg1 && arg1.data ? arg1 : arg2;
  const userAnswer = arg1 && arg1.data ? arg2 : arg1;

  const parsed = parseGeneralUserRaw(userAnswer);
  if (parsed) {
    const raw = getRaw(userAnswer);
    const usedDecimal = /[.,]\d/.test(raw);
    const tol = usedDecimal ? 1e-2 : 1e-9;

    const okA = Math.abs(parsed.a - task.data.a) < tol;
    const okB = Math.abs(parsed.b - task.data.b) < tol;
    const okC = Math.abs(parsed.c - task.data.c) < tol;
    if (okA && okB && okC) return true;
  }
  return numericallyEqGeneral(userAnswer, task.data.a, task.data.b, task.data.c, 1e-3);
}

export function renderUserAnswer(arg1, arg2) {
  const looksLikeTask = (x) => x && typeof x === "object" && x.data;
  const userAnswer = looksLikeTask(arg1) ? arg2 : arg1;

  const raw = getRaw(userAnswer);
  if (!raw || !raw.trim()) return <i>(brak odpowiedzi)</i>;

  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <InlineMath math={`f(x) = ${toLatexFractionSafe(raw)}`} />
    </div>
  );
}

export function renderAnswer({ data }) {
  const { a, b, c } = data;
  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <InlineMath math={`f(x) = ${generalLatex(a, b, c)}`} />
    </div>
  );
}

/* ---------- explanation ---------- */
export function renderExplanation({ data }) {
  const { a, b, c, x1, x2, deltaZero } = data;

  // Krok 1: postać iloczynowa
  const step1 = String.raw`f(x) = ${factorLatex(a, x1, x2)}`;

  // Krok 2:
  let lines;
  if (deltaZero) {
    // WZÓR SKRÓCONEGO MNOŻENIA — nie FOIL
    const r = x1;
    const innerB = -2 * r;
    const sgnB   = innerB >= 0 ? " + " : " - ";
    const absB   = Math.abs(innerB);
    const r2     = r*r;

    lines = String.raw`
\begin{aligned}
f(x) &= ${par(a)}\,(x${r<0?`+${Math.abs(r)}`:`-${r}`})^{2} \\[6pt]
     &= ${par(a)}\,(x^{2}${sgnB}${absB}x + ${r2}) \\[6pt]
     &= ${generalLatex(a, a*innerB, a*r2)}
\end{aligned}`;
  } else {
    // FOIL dla dwóch różnych nawiasów
    const A = Math.abs(x1);
    const B = Math.abs(x2);
    const s1 = (x1 < 0) ? +1 : -1;
    const s2 = (x2 < 0) ? +1 : -1;

    const t2Sign = s2 >= 0 ? " + " : " - ";
    const t3Sign = s1 >= 0 ? " + " : " - ";
    const prodSign = (s1*s2) >= 0 ? " + " : " - ";

    const mid = s1*A + s2*B;
    const midSign = mid >= 0 ? " + " : " - ";
    const prod = A*B;
    const prodTerm = `${prodSign}${prod}`;

    lines = String.raw`
\begin{aligned}
f(x) &= ${par(a)}\,(x${x1<0?`+${A}`:`-${A}`})(x${x2<0?`+${B}`:`-${B}`}) \\[6pt]
     &= ${par(a)}\,(x^{2}${t2Sign}${B}x ${t3Sign}${A}x ${prodTerm}) \\[6pt]
     &= ${par(a)}\,(x^{2}${midSign}${Math.abs(mid)}x ${prodTerm}) \\[6pt]
     &= ${generalLatex(a, a*mid, a*(s1*s2*prod))}
\end{aligned}`;
  }

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div><strong>Krok 1.</strong> Postać iloczynowa:</div>
      <BlockMath math={step1} />

      <div><strong>Krok 2.</strong> {deltaZero ? "Korzystamy ze wzoru skróconego mnożenia:" : "Rozwijamy nawiasy (FOIL) i redukujemy wyrazy liniowe:"}</div>
      <BlockMath math={lines} />

      <div><strong>Krok 3.</strong> Wynik w postaci ogólnej:</div>
      <BlockMath math={`f(x) = ${generalLatex(a, b, c)}`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderUserAnswer,
  renderAnswer,
  renderExplanation,
};
